import {
  Button,
  Input,
  Modal,
  notify,
  Skeleton,
  Switch,
  useConfirmModal,
} from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import {
  McpCredentialService,
  ServerService,
  type UserMcpCredential,
} from '@affine/core/modules/cloud';
import type { WorkspaceMetadata } from '@affine/core/modules/workspace';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { McpAccessMode, McpAccessScope } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './user-mcp-credentials-panel.css';

type GrantMode = McpAccessMode.READ_ONLY | McpAccessMode.READ_WRITE | 'NONE';
type WorkspaceOption = Pick<WorkspaceMetadata, 'id'> & { name: string };
type GrantDraftEntry = {
  mode: GrantMode;
  /** Phase-two capabilities this screen does not edit, but must not discard. */
  preservedScopes: McpAccessScope[];
};
type GrantDraft = Record<string, GrantDraftEntry>;
type RevealedCredential = {
  credential: UserMcpCredential;
  token: string;
};

const READ_SCOPES = [McpAccessScope.DOCUMENT_READ];
const WRITE_SCOPES = [
  McpAccessScope.DOCUMENT_READ,
  McpAccessScope.DOCUMENT_WRITE,
];
const DOCUMENT_ACCESS_SCOPES = new Set<McpAccessScope>([
  McpAccessScope.DOCUMENT_READ,
  McpAccessScope.DOCUMENT_WRITE,
  McpAccessScope.BLOB_WRITE,
]);

const scopesForMode = (mode: GrantMode) => {
  switch (mode) {
    case McpAccessMode.READ_WRITE:
      return WRITE_SCOPES;
    case McpAccessMode.READ_ONLY:
      return READ_SCOPES;
    case 'NONE':
      return [];
  }
};

const modeForScopes = (scopes: readonly McpAccessScope[]): GrantMode =>
  scopes.includes(McpAccessScope.DOCUMENT_WRITE)
    ? McpAccessMode.READ_WRITE
    : scopes.includes(McpAccessScope.DOCUMENT_READ)
      ? McpAccessMode.READ_ONLY
      : 'NONE';

const grantDraftForScopes = (
  scopes: readonly McpAccessScope[]
): GrantDraftEntry => ({
  mode: modeForScopes(scopes),
  preservedScopes: scopes.filter(scope => !DOCUMENT_ACCESS_SCOPES.has(scope)),
});

const scopesForGrantDraft = (draft: GrantDraftEntry) => [
  ...scopesForMode(draft.mode),
  ...draft.preservedScopes,
];

export const endpointFor = (baseUrl: string, workspaceId: string) => {
  const endpoint = new URL(baseUrl);
  const basePath = endpoint.pathname.replace(/\/+$/, '');
  endpoint.pathname = `${basePath}/api/workspaces/${encodeURIComponent(
    workspaceId
  )}/mcp`;
  endpoint.search = '';
  endpoint.hash = '';
  return endpoint.toString();
};

const staticResourceUrlFor = (baseUrl: string, path: string) => {
  const url = new URL(baseUrl);
  const basePath = url.pathname.replace(/\/+$/, '');
  url.pathname = `${basePath}/${path.replace(/^\/+/, '')}`;
  url.search = '';
  url.hash = '';
  return url.toString();
};

const mcpConfigFor = (
  baseUrl: string,
  credential: UserMcpCredential,
  token?: string
) => {
  const servers = Object.fromEntries(
    credential.workspaceGrants
      .filter(grant => !grant.revokedAt)
      .map(grant => [
        `affine_workspace_${grant.workspaceId}`,
        {
          type: 'streamable-http',
          url: endpointFor(baseUrl, grant.workspaceId),
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        },
      ])
  );
  return JSON.stringify({ mcpServers: servers }, null, 2);
};

export const agentPromptFor = (
  baseUrl: string,
  credential: UserMcpCredential,
  token: string,
  skillKind: 'mcp' | 'documentation' = 'mcp'
) => {
  const isDocumentationSkill = skillKind === 'documentation';
  const skillUrl = staticResourceUrlFor(
    baseUrl,
    isDocumentationSkill
      ? 'mcp/cangjie-affine-skill.zip'
      : 'mcp/cangjie-affine/SKILL.md'
  );
  const clientUrl = staticResourceUrlFor(
    baseUrl,
    'mcp/cangjie-affine/client-codex.md'
  );
  const endpoints = Object.fromEntries(
    credential.workspaceGrants
      .filter(grant => !grant.revokedAt)
      .map(grant => [
        grant.workspaceId,
        endpointFor(baseUrl, grant.workspaceId),
      ])
  );
  const primaryEndpoint =
    endpoints[credential.workspaceId] ??
    endpointFor(baseUrl, credential.workspaceId);

  if (isDocumentationSkill) {
    return [
      '请安装并使用 Cangjie.Affine Skill，自由地帮我读取、搜索、编写和管理 cangjie.affine 文档。',
      `Cangjie.Affine Skill 下载链接：${skillUrl}`,
      '请下载并解压这个 Skill 压缩包，加载其中的 SKILL.md，并按其中的步骤配置和使用脚本。',
      '以下是我的私有连接信息。请将它们写入当前用户的安全环境变量或密钥存储，不要提交到仓库、日志、截图或公开内容。',
      `CANGJIE_AFFINE_TOKEN=${token}\nCANGJIE_AFFINE_ENDPOINT=${primaryEndpoint}\nCANGJIE_AFFINE_ENDPOINTS=${JSON.stringify(endpoints)}`,
      '使用这个 Skill 读取、搜索、创建、编辑、重命名、移动、软删除和恢复文档，查看历史版本，读取和管理评论/回复，读取表格，并按 Skill 中的接口执行附件和表格操作。实际可用工具取决于凭证权限和 tools/list；如果授予对应权限，也可以操作原生块、批量、事件和 Webhook。更新已有文档时，先读取文档版本，再用 Markdown 文件和该版本执行 update。',
    ].join('\n\n');
  }

  return [
    '请安装并配置 Cangjie.Affine MCP Skill，连接我的 cangjie.affine 工作区。',
    `MCP Skill 下载链接：${skillUrl}\n客户端配置参考：${clientUrl}`,
    '以下是我的私有连接信息。请将 token 写入当前用户的安全环境变量或密钥存储，不要提交到仓库、日志、截图或公开内容。',
    `AFFINE_MCP_TOKEN=${token}\nAFFINE_MCP_ENDPOINT=${primaryEndpoint}\nAFFINE_MCP_ENDPOINTS=${JSON.stringify(endpoints)}`,
    `请按 MCP 客户端需要配置下面的连接信息：\n${mcpConfigFor(baseUrl, credential, token)}`,
    '配置完成后先初始化并发现工具，只使用实际可用的工具。先搜索或读取文档再修改；创建、编辑、移动、删除/恢复、上传附件、原生块、批量操作和 Webhook 都必须先取得我的明确确认。',
  ].join('\n\n');
};

const copyText = async (value: string, successTitle: string) => {
  await navigator.clipboard.writeText(value);
  notify.success({ title: successTitle });
};

const statusLabel = (
  t: ReturnType<typeof useI18n>,
  status: UserMcpCredential['status']
) => {
  switch (status) {
    case 'ACTIVE':
      return t['com.affine.settings.account.mcp.status.active']();
    case 'ROTATING':
      return t['com.affine.settings.account.mcp.status.rotating']();
    case 'EXPIRING':
      return t['com.affine.settings.account.mcp.status.expiring']();
    case 'EXPIRED':
      return t['com.affine.settings.account.mcp.status.expired']();
    case 'REVOKED':
      return t['com.affine.settings.account.mcp.status.revoked']();
    default:
      return status;
  }
};

const isTerminalCredential = (credential: UserMcpCredential) =>
  credential.status === 'REVOKED' || credential.status === 'EXPIRED';

const modeLabel = (t: ReturnType<typeof useI18n>, mode: GrantMode) =>
  mode === McpAccessMode.READ_WRITE
    ? t['com.affine.settings.account.mcp.access.read-write']()
    : mode === McpAccessMode.READ_ONLY
      ? t['com.affine.settings.account.mcp.access.read-only']()
      : t['com.affine.settings.account.mcp.access.none']();

const scopeLabel = (t: ReturnType<typeof useI18n>, scope: McpAccessScope) => {
  switch (scope) {
    case McpAccessScope.DOCUMENT_READ:
      return t['com.affine.settings.account.mcp.scope.document-read']();
    case McpAccessScope.DOCUMENT_WRITE:
      return t['com.affine.settings.account.mcp.scope.document-write']();
    case McpAccessScope.BLOB_WRITE:
      return t['com.affine.settings.account.mcp.scope.blob-write']();
    case McpAccessScope.BLOCK_READ:
      return t['com.affine.settings.account.mcp.scope.block-read']();
    case McpAccessScope.BLOCK_WRITE:
      return t['com.affine.settings.account.mcp.scope.block-write']();
    case McpAccessScope.BATCH_WRITE:
      return t['com.affine.settings.account.mcp.scope.batch-write']();
    case McpAccessScope.EVENT_READ:
      return t['com.affine.settings.account.mcp.scope.event-read']();
    case McpAccessScope.WEBHOOK_MANAGE:
      return t['com.affine.settings.account.mcp.scope.webhook-manage']();
  }
};

const WorkspaceGrantEditor = ({
  options,
  primaryWorkspaceId,
  value,
  onChange,
  allowNoDocumentAccess,
}: {
  options: WorkspaceOption[];
  primaryWorkspaceId: string;
  value: GrantDraft;
  onChange: (next: GrantDraft) => void;
  allowNoDocumentAccess: boolean;
}) => {
  const t = useI18n();

  const toggleWorkspace = useCallback(
    (workspaceId: string, checked: boolean) => {
      if (workspaceId === primaryWorkspaceId && !checked) return;
      const next = { ...value };
      if (checked) {
        next[workspaceId] ??= grantDraftForScopes(WRITE_SCOPES);
      } else delete next[workspaceId];
      onChange(next);
    },
    [onChange, primaryWorkspaceId, value]
  );

  return (
    <div className={styles.workspaceList}>
      {options.map(option => {
        const draft = value[option.id];
        const selected = !!draft;
        const mode = draft?.mode ?? McpAccessMode.READ_ONLY;
        return (
          <div className={styles.workspaceRow} key={option.id}>
            <div className={styles.workspaceChoice}>
              <Switch
                aria-label={option.name}
                checked={selected}
                className={styles.workspaceToggle}
                data-testid={`mcp-workspace-toggle-${option.id}`}
                disabled={option.id === primaryWorkspaceId}
                size={28}
                onChange={checked => toggleWorkspace(option.id, checked)}
              />
              <span>
                {option.name}
                <span className={styles.workspaceId}> ({option.id})</span>
              </span>
              {draft?.preservedScopes.map(scope => (
                <span className={styles.scope} key={scope}>
                  {scopeLabel(t, scope)}
                </span>
              ))}
            </div>
            <select
              className={styles.select}
              disabled={!selected}
              value={mode}
              aria-label={t['com.affine.settings.account.mcp.field.access']()}
              onChange={event =>
                onChange({
                  ...value,
                  [option.id]: {
                    mode: event.currentTarget.value as GrantMode,
                    preservedScopes: draft?.preservedScopes ?? [],
                  },
                })
              }
            >
              {allowNoDocumentAccess ? (
                <option value="NONE">{modeLabel(t, 'NONE')}</option>
              ) : null}
              <option value={McpAccessMode.READ_ONLY}>
                {modeLabel(t, McpAccessMode.READ_ONLY)}
              </option>
              <option value={McpAccessMode.READ_WRITE}>
                {modeLabel(t, McpAccessMode.READ_WRITE)}
              </option>
            </select>
          </div>
        );
      })}
      {Object.values(value).some(draft => draft.preservedScopes.length) ? (
        <div className={styles.description}>
          {t['com.affine.settings.account.mcp.scope.preserved']()}
        </div>
      ) : null}
    </div>
  );
};

const CredentialRevealModal = ({
  revealed,
  baseUrl,
  skillKind,
  onClose,
}: {
  revealed: RevealedCredential | null;
  baseUrl: string;
  skillKind: 'mcp' | 'documentation';
  onClose: () => void;
}) => {
  const t = useI18n();
  const prompt = revealed
    ? agentPromptFor(baseUrl, revealed.credential, revealed.token, skillKind)
    : '';
  return (
    <Modal
      open={!!revealed}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      contentOptions={{ className: styles.modal }}
    >
      {revealed ? (
        <>
          <div className={styles.modalTitle}>
            {t['com.affine.settings.account.mcp.reveal.title']()}
          </div>
          <div className={styles.description}>
            {t['com.affine.settings.account.mcp.reveal.warning']()}
          </div>
          <div className={styles.field}>
            <span>{t['com.affine.settings.account.mcp.reveal.prompt']()}</span>
            <pre className={styles.config} data-testid="mcp-agent-prompt">
              {prompt}
            </pre>
            <Button
              onClick={() => {
                void copyText(
                  prompt,
                  t['com.affine.settings.account.mcp.copied']()
                ).catch(error => {
                  notify.error({
                    title: t['com.affine.settings.account.mcp.copy-failed'](),
                    message: String(error),
                  });
                });
              }}
            >
              {t['com.affine.settings.account.mcp.action.copy-prompt']()}
            </Button>
          </div>
          <div className={styles.modalActions}>
            <Button variant="primary" onClick={onClose}>
              {t['com.affine.settings.account.mcp.action.done']()}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
};

export const UserMcpCredentialsPanel = ({
  skillKind = 'mcp',
}: {
  skillKind?: 'mcp' | 'documentation';
}) => {
  const t = useI18n();
  const credentialsService = useService(McpCredentialService);
  const workspacesService = useService(WorkspacesService);
  const serverService = useService(ServerService);
  const { openConfirmModal } = useConfirmModal();
  const metadata = useLiveData(workspacesService.list.workspaces$);
  const profiles = workspacesService.getAllWorkspaceProfile();
  const credentials = useLiveData(credentialsService.userCredentials$);
  const loading = useLiveData(credentialsService.userLoading$);
  const error = useLiveData(credentialsService.userError$);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [primaryWorkspaceId, setPrimaryWorkspaceId] = useState('');
  const [createGrants, setCreateGrants] = useState<GrantDraft>({});
  const [editing, setEditing] = useState<UserMcpCredential | null>(null);
  const [editGrants, setEditGrants] = useState<GrantDraft>({});
  const [revealed, setRevealed] = useState<RevealedCredential | null>(null);
  const [mutating, setMutating] = useState<string | null>(null);

  const options = useMemo<WorkspaceOption[]>(() => {
    const profileNames = new Map(
      profiles.map(profile => [profile.id, profile.profile$.value?.name])
    );
    return metadata
      .filter(
        workspace =>
          workspace.flavour === serverService.server.id &&
          workspace.flavour !== 'local'
      )
      .map(workspace => ({
        ...workspace,
        name:
          workspace.name?.trim() ||
          profileNames.get(workspace.id)?.trim() ||
          workspace.id,
      }));
  }, [metadata, profiles, serverService.server.id]);

  useEffect(() => {
    credentialsService.revalidateForUser().catch(() => undefined);
  }, [credentialsService]);

  useEffect(() => {
    if (!primaryWorkspaceId && options.length) {
      const first = options[0].id;
      setPrimaryWorkspaceId(first);
      setCreateGrants(
        Object.fromEntries(
          options.map(option => [option.id, grantDraftForScopes(WRITE_SCOPES)])
        )
      );
    }
  }, [options, primaryWorkspaceId]);

  const resetCreate = useCallback(() => {
    setCreateOpen(false);
    setName('');
    if (options.length) {
      setPrimaryWorkspaceId(options[0].id);
      setCreateGrants(
        Object.fromEntries(
          options.map(option => [option.id, grantDraftForScopes(WRITE_SCOPES)])
        )
      );
    } else {
      setPrimaryWorkspaceId('');
      setCreateGrants({});
    }
  }, [options]);

  const create = useCallback(async () => {
    if (!name.trim() || !primaryWorkspaceId) return;
    const grants = Object.entries(createGrants).map(([workspaceId, draft]) => ({
      workspaceId,
      scopes: scopesForGrantDraft(draft),
    }));
    if (!grants.some(grant => grant.workspaceId === primaryWorkspaceId)) {
      grants.push({
        workspaceId: primaryWorkspaceId,
        scopes: scopesForGrantDraft(grantDraftForScopes(WRITE_SCOPES)),
      });
    }
    setMutating('create');
    try {
      const result = await credentialsService.createForUser({
        workspaceId: primaryWorkspaceId,
        name: name.trim(),
        accessMode:
          createGrants[primaryWorkspaceId]?.mode === McpAccessMode.READ_WRITE
            ? McpAccessMode.READ_WRITE
            : McpAccessMode.READ_ONLY,
        scopes: scopesForGrantDraft(
          createGrants[primaryWorkspaceId] ?? grantDraftForScopes(WRITE_SCOPES)
        ),
        workspaceGrants: grants,
        expirationDays: 90,
      });
      setRevealed(result);
      resetCreate();
    } catch (cause) {
      notify.error({
        title: t['com.affine.settings.account.mcp.create-failed'](),
        message: String(cause),
      });
    } finally {
      setMutating(null);
    }
  }, [
    createGrants,
    credentialsService,
    name,
    primaryWorkspaceId,
    resetCreate,
    t,
  ]);

  const openEdit = useCallback((credential: UserMcpCredential) => {
    const grants = Object.fromEntries(
      credential.workspaceGrants
        .filter(grant => !grant.revokedAt)
        .map(grant => [grant.workspaceId, grantDraftForScopes(grant.scopes)])
    );
    grants[credential.workspaceId] ??= grantDraftForScopes(READ_SCOPES);
    setEditGrants(grants);
    setEditing(credential);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editing) return;
    const grants = Object.entries(editGrants).map(([workspaceId, draft]) => ({
      workspaceId,
      scopes: scopesForGrantDraft(draft),
    }));
    setMutating(editing.id);
    try {
      await credentialsService.updateWorkspaceGrantsForUser(editing.id, grants);
      setEditing(null);
    } catch (cause) {
      notify.error({
        title: t['com.affine.settings.account.mcp.update-failed'](),
        message: String(cause),
      });
    } finally {
      setMutating(null);
    }
  }, [credentialsService, editGrants, editing, t]);

  const rotate = useCallback(
    async (credential: UserMcpCredential) => {
      setMutating(credential.id);
      try {
        setRevealed(await credentialsService.rotateForUser(credential.id, 90));
      } catch (cause) {
        notify.error({
          title: t['com.affine.settings.account.mcp.rotate-failed'](),
          message: String(cause),
        });
      } finally {
        setMutating(null);
      }
    },
    [credentialsService, t]
  );

  const reveal = useCallback(
    async (credential: UserMcpCredential) => {
      setMutating(credential.id);
      try {
        setRevealed(await credentialsService.revealForUser(credential.id));
      } catch (cause) {
        notify.error({
          title: t['com.affine.settings.account.mcp.reveal-failed'](),
          message: String(cause),
        });
      } finally {
        setMutating(null);
      }
    },
    [credentialsService, t]
  );

  const revoke = useCallback(
    (credential: UserMcpCredential) => {
      openConfirmModal({
        title: t['com.affine.settings.account.mcp.action.revoke'](),
        description: t['com.affine.settings.account.mcp.revoke-confirm']({
          name: credential.name,
        }),
        confirmText: t['com.affine.settings.account.mcp.action.revoke'](),
        cancelText: t.Cancel(),
        confirmButtonOptions: { variant: 'error' },
        onConfirm: async () => {
          setMutating(credential.id);
          try {
            await credentialsService.revokeForUser(credential.id);
          } catch (cause) {
            notify.error({
              title: t['com.affine.settings.account.mcp.revoke-failed'](),
              message: String(cause),
            });
          } finally {
            setMutating(null);
          }
        },
      });
    },
    [credentialsService, openConfirmModal, t]
  );

  const deleteCredential = useCallback(
    (credential: UserMcpCredential) => {
      openConfirmModal({
        title: t['com.affine.settings.account.mcp.action.delete'](),
        description: t['com.affine.settings.account.mcp.delete-confirm']({
          name: credential.name,
        }),
        confirmText: t['com.affine.settings.account.mcp.action.delete'](),
        cancelText: t.Cancel(),
        confirmButtonOptions: { variant: 'error' },
        onConfirm: async () => {
          setMutating(credential.id);
          try {
            await credentialsService.deleteForUser(credential.id);
          } catch (cause) {
            notify.error({
              title: t['com.affine.settings.account.mcp.delete-failed'](),
              message: String(cause),
            });
          } finally {
            setMutating(null);
          }
        },
      });
    },
    [credentialsService, openConfirmModal, t]
  );

  const workspaceName = useCallback(
    (workspaceId: string) =>
      options.find(option => option.id === workspaceId)?.name ?? workspaceId,
    [options]
  );

  const editingOptions = useMemo<WorkspaceOption[]>(() => {
    if (!editing) return options;
    const knownWorkspaceIds = new Set(options.map(option => option.id));
    const grantWorkspaceIds = new Set([
      editing.workspaceId,
      ...editing.workspaceGrants
        .filter(grant => !grant.revokedAt)
        .map(grant => grant.workspaceId),
    ]);
    return [
      ...options,
      ...[...grantWorkspaceIds]
        .filter(workspaceId => !knownWorkspaceIds.has(workspaceId))
        .map(workspaceId => ({ id: workspaceId, name: workspaceId })),
    ];
  }, [editing, options]);

  return (
    <SettingRow
      name={t['com.affine.settings.account.mcp.title']()}
      desc={t['com.affine.settings.account.mcp.description']()}
      spreadCol={false}
    >
      <div className={styles.panel} data-testid="user-mcp-credentials-panel">
        <div className={styles.description}>
          {t['com.affine.settings.account.mcp.disclosure']()}
        </div>
        {error ? (
          <div className={styles.error}>
            {t['com.affine.settings.account.mcp.load-failed']()}
          </div>
        ) : null}
        {loading && !credentials ? (
          <div className={styles.credentialList}>
            <Skeleton variant="rounded" height={96} />
          </div>
        ) : null}
        {!loading && credentials?.length === 0 ? (
          <div className={styles.description}>
            {t['com.affine.settings.account.mcp.empty']()}
          </div>
        ) : null}
        {credentials?.length ? (
          <div className={styles.credentialList}>
            {credentials.map(credential => {
              const terminal = isTerminalCredential(credential);
              const busy = mutating === credential.id;
              return (
                <div className={styles.credential} key={credential.id}>
                  <div className={styles.credentialHeader}>
                    <div>
                      <div className={styles.credentialTitle}>
                        {credential.name}
                      </div>
                      <div className={styles.description}>
                        {t['com.affine.settings.account.mcp.primary']({
                          workspace: workspaceName(credential.workspaceId),
                        })}
                      </div>
                    </div>
                    <span className={styles.status}>
                      {statusLabel(t, credential.status)}
                    </span>
                  </div>
                  {terminal ? (
                    <>
                      <div
                        className={styles.description}
                        data-testid={`user-mcp-terminal-${credential.id}`}
                      >
                        {t['com.affine.settings.account.mcp.terminal']()}
                      </div>
                      <div className={styles.actions}>
                        <Button
                          variant="error"
                          disabled={busy}
                          loading={busy}
                          data-testid={`user-mcp-delete-${credential.id}`}
                          onClick={() => {
                            deleteCredential(credential);
                          }}
                        >
                          {t['com.affine.settings.account.mcp.action.delete']()}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.grants}>
                        {credential.workspaceGrants.some(
                          grant => !grant.revokedAt
                        ) ? (
                          credential.workspaceGrants
                            .filter(grant => !grant.revokedAt)
                            .map(grant => (
                              <div
                                className={styles.grant}
                                key={grant.workspaceId}
                              >
                                <div className={styles.grantMeta}>
                                  <span>
                                    {workspaceName(grant.workspaceId)}
                                  </span>
                                  {grant.scopes.map(scope => (
                                    <span className={styles.scope} key={scope}>
                                      {scopeLabel(t, scope)}
                                    </span>
                                  ))}
                                </div>
                                <Button
                                  onClick={() => {
                                    void copyText(
                                      endpointFor(
                                        serverService.server.baseUrl,
                                        grant.workspaceId
                                      ),
                                      t[
                                        'com.affine.settings.account.mcp.copied'
                                      ]()
                                    ).catch(cause => {
                                      notify.error({
                                        title:
                                          t[
                                            'com.affine.settings.account.mcp.copy-failed'
                                          ](),
                                        message: String(cause),
                                      });
                                    });
                                  }}
                                >
                                  {t[
                                    'com.affine.settings.account.mcp.action.copy-endpoint'
                                  ]()}
                                </Button>
                              </div>
                            ))
                        ) : (
                          <div className={styles.description}>
                            {t[
                              'com.affine.settings.account.mcp.no-active-grants'
                            ]()}
                          </div>
                        )}
                      </div>
                      <div className={styles.actions}>
                        {credential.secretAvailable === true ? (
                          <Button
                            disabled={busy}
                            onClick={() => {
                              void reveal(credential).catch(() => undefined);
                            }}
                            data-testid={`user-mcp-reveal-${credential.id}`}
                          >
                            {t[
                              'com.affine.settings.account.mcp.action.reveal'
                            ]()}
                          </Button>
                        ) : (
                          <span className={styles.description}>
                            {t[
                              'com.affine.settings.account.mcp.reveal.legacy'
                            ]()}
                          </span>
                        )}
                        <Button
                          disabled={busy}
                          onClick={() => openEdit(credential)}
                        >
                          {t[
                            'com.affine.settings.account.mcp.action.edit-grants'
                          ]()}
                        </Button>
                        <Button
                          disabled={busy}
                          loading={busy}
                          onClick={() => {
                            void rotate(credential).catch(() => undefined);
                          }}
                        >
                          {t['com.affine.settings.account.mcp.action.rotate']()}
                        </Button>
                        <Button
                          variant="error"
                          disabled={busy}
                          onClick={() => {
                            revoke(credential);
                          }}
                        >
                          {t['com.affine.settings.account.mcp.action.revoke']()}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
        {options.length ? (
          !createOpen ? (
            <Button
              variant="primary"
              data-testid="user-mcp-create"
              onClick={() => setCreateOpen(true)}
            >
              {t['com.affine.settings.account.mcp.action.create']()}
            </Button>
          ) : (
            <div className={styles.form} data-testid="user-mcp-create-form">
              <label className={styles.field}>
                <span>{t['com.affine.settings.account.mcp.field.name']()}</span>
                <Input
                  value={name}
                  maxLength={64}
                  autoFocus
                  onChange={setName}
                  placeholder={t[
                    'com.affine.settings.account.mcp.field.name-placeholder'
                  ]()}
                />
              </label>
              <label className={styles.field}>
                <span>
                  {t['com.affine.settings.account.mcp.field.primary']()}
                </span>
                <select
                  className={styles.select}
                  value={primaryWorkspaceId}
                  onChange={event => {
                    const next = event.currentTarget.value;
                    setPrimaryWorkspaceId(next);
                    setCreateGrants(current => ({
                      ...current,
                      [next]: current[next] ?? grantDraftForScopes(READ_SCOPES),
                    }));
                  }}
                >
                  {options.map(option => (
                    <option value={option.id} key={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className={styles.field}>
                <span>
                  {t['com.affine.settings.account.mcp.field.grants']()}
                </span>
                <WorkspaceGrantEditor
                  options={options}
                  primaryWorkspaceId={primaryWorkspaceId}
                  value={createGrants}
                  onChange={setCreateGrants}
                  allowNoDocumentAccess={false}
                />
              </div>
              <div className={styles.actions}>
                <Button onClick={resetCreate}>{t['Cancel']()}</Button>
                <Button
                  variant="primary"
                  disabled={
                    !name.trim() || !primaryWorkspaceId || mutating === 'create'
                  }
                  loading={mutating === 'create'}
                  data-testid="user-mcp-create-submit"
                  onClick={() => {
                    void create().catch(() => undefined);
                  }}
                >
                  {t['com.affine.settings.account.mcp.action.create']()}
                </Button>
              </div>
            </div>
          )
        ) : (
          <div className={styles.description}>
            {t['com.affine.settings.account.mcp.no-workspaces']()}
          </div>
        )}
      </div>
      <Modal
        open={!!editing}
        onOpenChange={open => {
          if (!open) setEditing(null);
        }}
        contentOptions={{ className: styles.modal }}
      >
        {editing ? (
          <>
            <div className={styles.modalTitle}>
              {t['com.affine.settings.account.mcp.edit.title']({
                name: editing.name,
              })}
            </div>
            <div className={styles.description}>
              {t['com.affine.settings.account.mcp.edit.description']()}
            </div>
            <WorkspaceGrantEditor
              options={editingOptions}
              primaryWorkspaceId={editing.workspaceId}
              value={editGrants}
              onChange={setEditGrants}
              allowNoDocumentAccess
            />
            <div className={styles.modalActions}>
              <Button onClick={() => setEditing(null)}>{t['Cancel']()}</Button>
              <Button
                variant="primary"
                loading={mutating === editing.id}
                disabled={mutating === editing.id}
                onClick={() => {
                  void saveEdit().catch(() => undefined);
                }}
              >
                {t['com.affine.settings.account.mcp.action.save-grants']()}
              </Button>
            </div>
          </>
        ) : null}
      </Modal>
      <CredentialRevealModal
        revealed={revealed}
        baseUrl={serverService.server.baseUrl}
        skillKind={skillKind}
        onClose={() => setRevealed(null)}
      />
    </SettingRow>
  );
};
